"use client";

import { type FC, useEffect, useState } from "react";
import type { IAsset } from "@api/IWalletController";
import { TonApi } from "@api/TonApi";
import { TraceIDs } from "@ton-api/client";

interface TokenAssetProps {
    asset: IAsset | string | null | undefined;
}

const STONFI_LISTING_PRICE = 1500;

const TokenAsset: FC<TokenAssetProps> = ({ asset }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [progress, setProgress] = useState<number | null>(null);
    const [traces, setTraces] = useState<TraceIDs | null>(null);
    const [lastChecked, setLastChecked] = useState<Date | null>(null);

    useEffect(() => {
        if (!asset) return;
        if (!showTooltip) return;
        const iasset = asset as IAsset;
        const fetchTonBalance = async () => {
            setIsLoading(true);
            try {
                const tonApi = new TonApi();

                const balance = await tonApi.getTonBalance(iasset.jettonMasterAddress);
                const traces = await tonApi.getTraces(iasset.jettonMasterAddress);

                setTraces(traces);
                setProgress(Number(balance.balance) * 10 ** -9);
                // Update the last checked timestamp

                setLastChecked(new Date());
            } catch (error) {
                console.error("Failed to fetch balance:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTonBalance();
    }, [asset, showTooltip]);

    // Handle loading state or missing asset
    if (!asset) {
        return (
            <div className="flex items-center w-fit ">
                <div className="w-6 h-6 rounded-full bg-gray-600 animate-pulse mr-2"></div>
                <span className="text-gray-400">Loading...</span>
            </div>
        );
    }

    // Handle string asset (GUID that hasn't been resolved yet)
    if (typeof asset === "string") {
        return (
            <div className="flex items-center w-fit">
                <div className="w-6 h-6 rounded-full bg-gray-600 animate-pulse mr-2"></div>
                <span className="text-gray-400">Loading asset...</span>
            </div>
        );
    }

    // Function to format address for display
    const formatAddress = (address: string) => {
        if (!address) return "";
        return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
    };

    // Format the timestamp in a user-friendly way
    const formatTimestamp = (date: Date | null) => {
        if (!date) return "Never checked";

        // Format as HH:MM:SS
        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");
        const seconds = date.getSeconds().toString().padStart(2, "0");

        return `${hours}:${minutes}:${seconds}`;
    };

    // Calculate progress percentage if progress is provided
    const progressPercentage = progress ? (progress / STONFI_LISTING_PRICE) * 100 : 0;

    // Render the actual asset with tooltip
    return (
        <div className="flex flex-col w-fit">
            <div
                className="flex items-center relative group p-1 rounded-md bg-card pr-3"
                style={{
                    backgroundColor: "#3B3E43B2",
                    borderRadius: "46px",
                    borderColor: "#4C4F55B2",
                    borderWidth: "1px",
                }}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
            >
                {asset.image ? (
                    <div className="relative w-6 h-6 rounded-full bg-gray-600 mr-2 flex items-center justify-center text-xs">
                        <div className="w-6 h-6 rounded-full bg-gray-700 mr-2 flex items-center justify-center text-xs absolute top-0 left-0 z-1">
                            {asset.symbol?.charAt(0) || "?"}
                        </div>
                        <img
                            src={asset.image || "/placeholder.svg"}
                            alt={asset.name || "Token"}
                            width={24}
                            height={24}
                            className="rounded-full mr-2 absolute top-0 left-0 z-10"
                            onError={(e) => {
                                // Fallback for failed image loads
                                e.currentTarget.src = "/images/jettonFallback.svg";
                            }}
                        />
                    </div>
                ) : (
                    <div className="w-6 h-6 rounded-full bg-gray-700 mr-2 flex items-center justify-center text-xs">
                        {asset.symbol?.charAt(0) || "?"}
                    </div>
                )}
                <span>{asset.symbol || "Unknown Token"}</span>

                {showTooltip && (
                    <div
                        className="absolute left-full top-0 ml-2 z-50 w-60 rounded-md shadow-lg bg-card border border-gray-700 text-white text-xs
                     transition-all duration-200 ease-in-out transform origin-left "
                        style={{ opacity: showTooltip ? 1 : 0, transform: showTooltip ? "scale(1)" : "scale(0.95)" }}
                    >
                        <div className="p-2 space-y-1.5">
                            {/* Header with image, name and verification */}
                            <div className="flex items-center justify-between border-b border-gray-700 pb-1.5">
                                <div className="flex items-center relative w-full">
                                    {asset.image && (
                                        <img
                                            src={asset.image || "/placeholder.svg"}
                                            alt={asset.symbol}
                                            className="w-6 h-6 rounded-full mr-1.5"
                                            onError={(e) => {
                                                e.currentTarget.src = "/images/jettonFallback.svg";
                                            }}
                                        />
                                    )}
                                    <div>
                                        <div className="font-medium text-xs">{asset.name}</div>
                                        <div className="text-gray-400 text-xs">{asset.symbol}</div>
                                    </div>
                                    <div className="bg-gray-700 p-1.5 rounded text-xs break-all right-0 absolute top-0">
                                        {formatAddress(asset.jettonMasterAddress)}
                                    </div>
                                </div>
                                {asset.isVerified && (
                                    <div className="bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-sm">✓</div>
                                )}
                            </div>

                            {asset.description && (
                                <div className="text-xs">
                                    <div className="text-gray-400 mb-0.5">Description:</div>
                                    <div className="bg-gray-700 p-1.5 rounded text-xs max-h-16 overflow-y-auto">
                                        {asset.description.length > 100
                                            ? `${asset.description.substring(0, 100)}...`
                                            : asset.description}
                                    </div>
                                </div>
                            )}

                            {progress !== null && (
                                <div className="mt-2 w-full">
                                    <div className="text-gray-400 text-xs mb-1">Before listing StonFi</div>
                                    <div className="flex items-center justify-between">
                                        <div className="w-full h-4 bg-gray-700 rounded-full overflow-hidden mr-3">
                                            <div
                                                className="h-full rounded-full"
                                                style={{
                                                    width: `${progressPercentage < 1 ? 1 : progressPercentage}%`,
                                                    background: "linear-gradient(90deg, #8C7DFF 0%, #7B68EE 100%)",
                                                }}
                                            ></div>
                                        </div>
                                        <div className="text-white whitespace-nowrap">
                                            {progress.toFixed(2)}/{STONFI_LISTING_PRICE}TON
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Footer with GUID and last checked time */}
                            <div className="flex justify-between items-center text-xs text-gray-500 pt-1 border-t border-gray-700">
                                <div className="flex items-center">
                                    {isLoading ? (
                                        <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse mr-1"></div>
                                    ) : (
                                        <div className="w-3 h-3 rounded-full bg-green-500 mr-1"></div>
                                    )}
                                    <span>Last checked: {formatTimestamp(lastChecked)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="absolute top-3 -left-2 w-3 h-3 bg-gray-800 border-l border-b border-gray-700 transform rotate-45"></div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TokenAsset;
